/**
 * @description Window, delta, chart, timezone, and account-type math for
 *   TowTech Scope 1 BI (LYN-395).
 *
 * @dependencies sendableCompanyId
 *
 * @notes Two IANA clocks: sales_organizations.timezone (ingest / pull
 *   window) and companies.timezone (Company view). All uses the logged-in
 *   user's zone. A location may default the company zone on create; it does
 *   not stay the clock. Do not infer the org zone from a location. Do not
 *   invent America/Phoenix. Stamps stay UTC — the zone is how a wall clock
 *   is parsed and how a chart is bucketed. Pipeline grains stay hour/day/week
 *   on the wire. Month, quarter, and year roll in the view zone. Dual late
 *   thresholds are UI params. All and Regular score every job against Regular
 *   late, including police. Police scores against Police late. Early is under
 *   half the active threshold; on time is through the threshold; late is over.
 *   Account Type All counts the loaded job rows in the display-zone civil
 *   window — same path as Police and Regular. Month volume is one civil day
 *   per point; quarter and year are one month per point. Early is under
 *   half the late minutes. Do not re-bucket UTC pipeline days. Do not
 *   rewrite PoliceDepartment. Do not match account name.
 */

import { isSendableCompanyId } from './sendableCompanyId';

export const MS_HOUR = 60 * 60 * 1000;
export const MS_DAY = 24 * MS_HOUR;
export const MS_WEEK = 7 * MS_DAY;
export const MAX_PIPELINE_MS = 400 * MS_DAY;
export const USUAL_WEEKS = 12;
export const LABELED_POINT_MAX = 24;

export const GRAINS = Object.freeze(['hour', 'day', 'week']);
export const TIME_FILTERS = Object.freeze(['hour', 'day', 'week', 'month', 'quarter', 'year']);
export const RESPONSE_ON_SCENE = 'onScene';
export const RESPONSE_COMPLETED = 'completed';
export const DEFAULT_LATE_THRESHOLD_MINUTES = 30;
export const DEFAULT_POLICE_LATE_MINUTES = 30;
export const DEFAULT_REGULAR_LATE_MINUTES = 45;
export const OVERVIEW_MIN_CALLS_DEFAULT = 1;
export const POLICE_ACCOUNT_TYPE = 'PoliceDepartment';
export const ACCOUNT_TYPE_ALL = 'all';
export const ACCOUNT_TYPE_REGULAR = 'regular';
export const VOLUME_STAMP = 'enroute';
export const TODAY_COLOR = '#F79009';
export const USUAL_COLOR = '#0BA5EC';
export const WEEKDAY_NAMES = Object.freeze([
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
]);
export const WEEKDAY_SHORT = Object.freeze(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']);
export const TIMELINE_LABELS = Object.freeze({
  hour: 'Last 24 hours',
  day: 'Last 7 days',
  week: 'Last 8 weeks',
  month: 'This month',
  quarter: 'This quarter',
  year: 'This year'
});
export const PERFORMANCE_TITLES = Object.freeze({
  hour: 'Hourly',
  day: 'Daily',
  week: 'Weekly',
  month: 'Monthly',
  quarter: 'Quarterly',
  year: 'Yearly'
});
export const COMPARISON_TITLES = Object.freeze({
  hour: 'Rolling hour vs. 12 week avg.',
  day: 'Weekday vs. 12 Week avg.',
  week: 'This week vs. Last 12 weeks',
  month: 'This month vs. last 4 months',
  quarter: 'This quarter vs. last 2 quarters',
  year: 'This year vs. prior years'
});

/**
 * @param {unknown} companyScope
 * @param {unknown} activeCompanyId
 * @returns {boolean}
 */
export function hasSessionCompany(companyScope, activeCompanyId) {
  return companyScope !== 'all' && isSendableCompanyId(activeCompanyId);
}

/**
 * Pack SO surface: a session company, or All with created companies on this SO.
 *
 * @param {unknown} companyScope
 * @param {unknown} activeCompanyId
 * @param {Array<{id?: unknown}>} [companies]
 * @returns {boolean}
 */
export function hasScope1Surface(companyScope, activeCompanyId, companies) {
  if (hasSessionCompany(companyScope, activeCompanyId)) return true;
  return companyScope === 'all' && salesOrgCompanies(companies).length > 0;
}

/**
 * Every created company on this sales org. Drops All / invalid ids.
 *
 * @param {Array<{id?: unknown}>} [companies]
 * @returns {Array<object>}
 */
export function salesOrgCompanies(companies) {
  return (companies || []).filter((row) => isSendableCompanyId(row?.id));
}

/**
 * @param {Date|string} value
 * @returns {Date}
 */
export function utcHourStart(value) {
  const d = value instanceof Date ? value : new Date(value);
  return new Date(Date.UTC(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    d.getUTCHours(),
    0,
    0,
    0
  ));
}

/**
 * @param {Date|string} value
 * @returns {Date}
 */
export function utcDayStart(value) {
  const d = value instanceof Date ? value : new Date(value);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0));
}

/**
 * ISO weekday: 1=Mon … 7=Sun. Matches profile=r12.
 *
 * @param {Date|string} value
 * @returns {number}
 */
export function isoWeekdayUtc(value) {
  const d = value instanceof Date ? value : new Date(value);
  const day = d.getUTCDay();
  return day === 0 ? 7 : day;
}

/**
 * ISO week start (Monday 00:00 UTC). Matches Postgres date_trunc('week').
 *
 * @param {Date|string} value
 * @returns {Date}
 */
export function utcWeekStart(value) {
  const start = utcDayStart(value);
  return new Date(start.getTime() - (isoWeekdayUtc(start) - 1) * MS_DAY);
}

/**
 * Half-open [from, to) for the grain. Includes the in-progress bucket.
 *
 * @param {'hour'|'day'|'week'} grain
 * @param {Date} [now]
 * @returns {{ from: Date, to: Date }}
 */
export function windowForGrain(grain, now = new Date()) {
  if (grain === 'hour') {
    const to = new Date(utcHourStart(now).getTime() + MS_HOUR);
    return { from: new Date(to.getTime() - 24 * MS_HOUR), to };
  }
  if (grain === 'day') {
    const to = new Date(utcDayStart(now).getTime() + MS_DAY);
    return { from: new Date(to.getTime() - 7 * MS_DAY), to };
  }
  if (grain === 'week') {
    const to = new Date(utcWeekStart(now).getTime() + MS_WEEK);
    return { from: new Date(to.getTime() - 8 * MS_WEEK), to };
  }
  throw new Error('grain must be hour, day, or week');
}

/**
 * @param {{ from: Date|string, to: Date|string }} window
 * @param {number} deltaMs
 * @returns {{ from: Date, to: Date }}
 */
export function shiftWindow({ from, to }, deltaMs) {
  return {
    from: new Date(new Date(from).getTime() + deltaMs),
    to: new Date(new Date(to).getTime() + deltaMs)
  };
}

/**
 * @param {{ from: Date, to: Date }} current
 * @returns {{ from: Date, to: Date }}
 */
export function wowWindow(current) {
  return shiftWindow(current, -MS_WEEK);
}

/**
 * @param {Date} [now]
 * @returns {{ from: Date, to: Date }}
 */
export function todayUtcWindow(now = new Date()) {
  const from = utcDayStart(now);
  return { from, to: new Date(from.getTime() + MS_DAY) };
}

/**
 * @param {Date} [now]
 * @returns {{ from: Date, to: Date }}
 */
export function sameDayLastWeekWindow(now = new Date()) {
  return shiftWindow(todayUtcWindow(now), -MS_WEEK);
}

/**
 * @param {Array<object>} buckets
 * @param {string} [key]
 * @returns {number}
 */
export function sumMeasure(buckets, key = 'enrouteCount') {
  if (!Array.isArray(buckets) || buckets.length === 0) return 0;
  return buckets.reduce((sum, bucket) => sum + (Number(bucket?.[key]) || 0), 0);
}

/**
 * @param {Array<object>} buckets
 * @returns {boolean}
 */
export function isEmptyVolume(buckets) {
  return sumMeasure(buckets, 'enrouteCount') === 0
    && sumMeasure(buckets, 'completedCount') === 0;
}

/**
 * @param {Array<object>} buckets
 * @param {string} valueKey
 * @param {string} weightKey
 * @returns {number|null}
 */
export function weightedAverage(buckets, valueKey, weightKey) {
  let weighted = 0;
  let weight = 0;
  for (const bucket of buckets || []) {
    const value = bucket?.[valueKey];
    const w = Number(bucket?.[weightKey]) || 0;
    if (value == null || Number.isNaN(Number(value)) || w <= 0) continue;
    weighted += Number(value) * w;
    weight += w;
  }
  if (weight === 0) return null;
  return weighted / weight;
}

/**
 * @param {Date|string} now
 * @param {number} hour
 * @returns {Date}
 */
export function r12AsOfForHour(now, hour) {
  const d = now instanceof Date ? now : new Date(now);
  const h = Number(hour);
  if (!Number.isInteger(h) || h < 0 || h > 23) {
    throw new Error('hour must be an integer 0-23');
  }
  return new Date(Date.UTC(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    h,
    0,
    0,
    0
  ));
}

/**
 * Missing current is not a real zero. Failed today vs last week is not -100%.
 *
 * @param {number|null|undefined} current
 * @param {number|null|undefined} previous
 * @returns {{ current: number|null, previous: number|null, absolute: number|null, percent: number|null, label: string }}
 */
export function deltaPair(current, previous) {
  if (current == null || Number.isNaN(Number(current))) {
    return {
      current: null,
      previous: previous == null || Number.isNaN(Number(previous)) ? null : Number(previous),
      absolute: null,
      percent: null,
      label: '—'
    };
  }
  const cur = Number(current);
  if (previous == null || Number.isNaN(Number(previous))) {
    return { current: cur, previous: null, absolute: null, percent: null, label: '—' };
  }
  const prev = Number(previous);
  const absolute = cur - prev;
  const percent = prev === 0 ? null : (absolute / prev) * 100;
  if (absolute === 0 && prev === 0) {
    return { current: cur, previous: prev, absolute, percent, label: '—' };
  }
  const sign = absolute > 0 ? '+' : '';
  const pctLabel = percent == null ? '' : ` (${sign}${Math.round(percent)}%)`;
  return {
    current: cur,
    previous: prev,
    absolute,
    percent,
    label: `${sign}${absolute}${pctLabel}`
  };
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function formatVolume(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (Math.abs(n - Math.round(n)) < 0.05) return String(Math.round(n));
  return n.toFixed(1);
}

/**
 * @param {unknown} seconds
 * @returns {string}
 */
export function formatSeconds(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  const total = Math.max(0, Math.round(Number(seconds)));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

/**
 * @param {unknown} minutes
 * @returns {number}
 */
export function minutesToSeconds(minutes) {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) return DEFAULT_LATE_THRESHOLD_MINUTES * 60;
  return Math.round(n * 60);
}

/**
 * @param {'onScene'|'completed'} metric
 * @returns {{ avgKey: string, weightKey: string, stamp: string }}
 */
export function responseKeys(metric) {
  if (metric === RESPONSE_COMPLETED) {
    return {
      avgKey: 'avgEnrouteToCompletedSeconds',
      weightKey: 'completedCount',
      stamp: 'completed'
    };
  }
  return {
    avgKey: 'avgEnrouteToOnSceneSeconds',
    weightKey: 'enrouteCount',
    stamp: 'enroute'
  };
}

/**
 * @param {object} job
 * @param {'onScene'|'completed'} metric
 * @returns {number|null}
 */
/**
 * Mean of the same durations late % uses. Do not weight bucket averages by
 * enrouteCount — that count includes in-flight jobs with no on-scene stamp.
 *
 * @param {Array<object>} jobs
 * @param {'onScene'|'completed'} metric
 * @returns {number|null}
 */
export function meanDuration(jobs, metric) {
  const durations = (jobs || [])
    .map((job) => jobDurationSeconds(job, metric))
    .filter((value) => value != null);
  if (durations.length === 0) return null;
  return durations.reduce((sum, value) => sum + value, 0) / durations.length;
}

/**
 * @param {object} job
 * @param {'onScene'|'completed'} metric
 * @returns {number|null}
 */
export function jobDurationSeconds(job, metric) {
  if (!job?.enrouteAt) return null;
  const start = Date.parse(job.enrouteAt);
  const endIso = metric === RESPONSE_COMPLETED ? job.completedAt : job.onSceneAt;
  if (!endIso) return null;
  const end = Date.parse(endIso);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
  return (end - start) / 1000;
}

/**
 * Late is at or over the threshold. Threshold is not stored.
 *
 * @param {Array<object>} jobs
 * @param {number} thresholdSeconds
 * @param {'onScene'|'completed'} metric
 * @returns {{ sample: number, late: number, rate: number|null }}
 */
export function lateRate(jobs, thresholdSeconds, metric) {
  const durations = (jobs || [])
    .map((job) => jobDurationSeconds(job, metric))
    .filter((value) => value != null);
  if (durations.length === 0) {
    return { sample: 0, late: 0, rate: null };
  }
  const late = durations.filter((value) => value > thresholdSeconds).length;
  return { sample: durations.length, late, rate: late / durations.length };
}

/**
 * @param {Array<object>} jobs
 * @param {number} thresholdSeconds
 * @param {'onScene'|'completed'} metric
 * @returns {{ sample: number, bins: Array<{ key: string, label: string, count: number }> }}
 */
export function durationDistribution(jobs, thresholdSeconds, metric) {
  const durations = (jobs || [])
    .map((job) => jobDurationSeconds(job, metric))
    .filter((value) => value != null);
  const halfLabel = formatSeconds(thresholdSeconds / 2);
  const bins = [
    { key: 'fast', label: `Under ${halfLabel}`, count: 0 },
    { key: 'onTime', label: 'On time', count: 0 },
    { key: 'late', label: 'Late', count: 0 }
  ];
  const half = thresholdSeconds / 2;
  for (const value of durations) {
    if (value < half) bins[0].count += 1;
    else if (value <= thresholdSeconds) bins[1].count += 1;
    else bins[2].count += 1;
  }
  return { sample: durations.length, bins };
}

/**
 * Exact DA token. Null / blank is Regular, not Police. Never match accountName.
 *
 * @param {object} job
 * @returns {string|null}
 */
export function jobAccountTypeToken(job) {
  const raw = job?.accountType;
  if (raw == null) return null;
  const token = String(raw);
  return token.trim() === '' ? null : token;
}

/**
 * @param {object} job
 * @returns {boolean}
 */
export function isPoliceJob(job) {
  return jobAccountTypeToken(job) === POLICE_ACCOUNT_TYPE;
}

/**
 * All = every job. Police = exact PoliceDepartment. Regular = everything else.
 * Any other selected value is the token itself.
 *
 * @param {object} job
 * @param {string} selected
 * @returns {boolean}
 */
export function matchesAccountType(job, selected) {
  if (!selected || selected === ACCOUNT_TYPE_ALL) return true;
  if (selected === ACCOUNT_TYPE_REGULAR) return !isPoliceJob(job);
  return jobAccountTypeToken(job) === selected;
}

/**
 * @param {Array<object>} jobs
 * @param {string} selected
 * @returns {Array<object>}
 */
export function filterJobsByAccountType(jobs, selected) {
  return (jobs || []).filter((job) => matchesAccountType(job, selected));
}

/**
 * Display-only label. Filter value stays the source token.
 *
 * @param {string} value
 * @returns {string}
 */
export function accountTypeLabel(value) {
  if (value === ACCOUNT_TYPE_ALL) return 'All';
  if (value === ACCOUNT_TYPE_REGULAR) return 'Regular';
  if (value === POLICE_ACCOUNT_TYPE) return 'Police';
  return String(value || '');
}

/**
 * All, Regular, Police, plus tokens already on the jobs. PoliceDepartment is
 * Police — it is not listed twice.
 *
 * @param {Array<object>} jobs
 * @returns {Array<{ value: string, label: string }>}
 */
export function accountTypeOptions(jobs) {
  const extras = new Set();
  for (const job of jobs || []) {
    const token = jobAccountTypeToken(job);
    if (token && token !== POLICE_ACCOUNT_TYPE) extras.add(token);
  }
  return [
    { value: ACCOUNT_TYPE_ALL, label: 'All' },
    { value: ACCOUNT_TYPE_REGULAR, label: 'Regular' },
    { value: POLICE_ACCOUNT_TYPE, label: 'Police' },
    ...[...extras].sort().map((token) => ({ value: token, label: accountTypeLabel(token) }))
  ];
}

/**
 * Police filter uses Police Late. All, Regular, and other tokens use Regular
 * Late — including police jobs on All.
 *
 * @param {string} accountType
 * @param {number} policeMinutes
 * @param {number} regularMinutes
 * @returns {number}
 */
export function lateMinutesForAccountType(accountType, policeMinutes, regularMinutes) {
  return accountType === POLICE_ACCOUNT_TYPE ? Number(policeMinutes) : Number(regularMinutes);
}

/**
 * @param {string} accountType
 * @returns {boolean}
 */
export function showsPoliceLateInput(accountType) {
  return accountType === POLICE_ACCOUNT_TYPE;
}

/**
 * Regular Late shows on All and Regular. It does not show on Police.
 *
 * @param {string} accountType
 * @returns {boolean}
 */
export function showsRegularLateInput(accountType) {
  return accountType !== POLICE_ACCOUNT_TYPE;
}

/**
 * Overview minimum calls. Whole number of 1 or more; anything else is 1.
 * Page state only.
 *
 * @param {unknown} value
 * @returns {number}
 */
export function parseMinCallCount(value) {
  const n = Number(value);
  if (!Number.isInteger(n) || n < 1) return OVERVIEW_MIN_CALLS_DEFAULT;
  return n;
}

/**
 * Police filter uses Police Late. All and Regular (and other tokens) use
 * Regular Late, including police jobs on All.
 *
 * @param {object} _job
 * @param {number} policeMinutes
 * @param {number} regularMinutes
 * @param {string} [accountType]
 * @returns {number}
 */
export function lateMinutesForJob(_job, policeMinutes, regularMinutes, accountType = ACCOUNT_TYPE_ALL) {
  return lateMinutesForAccountType(accountType, policeMinutes, regularMinutes);
}

/**
 * @param {object} job
 * @param {number} policeMinutes
 * @param {number} regularMinutes
 * @param {string} [accountType]
 * @returns {number}
 */
export function jobLateThresholdSeconds(job, policeMinutes, regularMinutes, accountType = ACCOUNT_TYPE_ALL) {
  return minutesToSeconds(lateMinutesForJob(job, policeMinutes, regularMinutes, accountType));
}

/**
 * Active threshold follows the Account Type filter, not the job token.
 * All scores police jobs against Regular late.
 *
 * @param {Array<object>} jobs
 * @param {number} policeMinutes
 * @param {number} regularMinutes
 * @param {'onScene'|'completed'} metric
 * @param {string} [accountType]
 * @returns {{ sample: number, late: number, rate: number|null }}
 */
export function lateRateByType(jobs, policeMinutes, regularMinutes, metric, accountType = ACCOUNT_TYPE_ALL) {
  void metric;
  const threshold = minutesToSeconds(
    lateMinutesForAccountType(accountType, policeMinutes, regularMinutes)
  );
  return lateRate(jobs, threshold, RESPONSE_ON_SCENE);
}

/**
 * Early under half the active threshold. On time through the threshold.
 * Late over the threshold. All uses Regular late.
 *
 * @param {Array<object>} jobs
 * @param {number} policeMinutes
 * @param {number} regularMinutes
 * @param {'onScene'|'completed'} metric
 * @param {string} [accountType]
 * @returns {{ sample: number, bins: Array<{ key: string, label: string, count: number, percent: number }> }}
 */
export function responsePieSlices(jobs, policeMinutes, regularMinutes, metric, accountType = ACCOUNT_TYPE_ALL) {
  void metric;
  const threshold = minutesToSeconds(
    lateMinutesForAccountType(accountType, policeMinutes, regularMinutes)
  );
  const dist = durationDistribution(jobs, threshold, RESPONSE_ON_SCENE);
  const labels = {
    fast: 'Early',
    onTime: 'On time',
    late: 'Late'
  };
  return {
    sample: dist.sample,
    bins: dist.bins.map((bin) => ({
      ...bin,
      label: labels[bin.key] || bin.label,
      percent: dist.sample === 0 ? 0 : bin.count / dist.sample
    }))
  };
}

/**
 * Mean of R12 slots (empty buckets included so the denominator stays 12).
 *
 * @param {Array<object>} buckets
 * @param {string} [key]
 * @returns {number}
 */
export function anticipatedFromR12(buckets, key = 'enrouteCount') {
  if (!Array.isArray(buckets) || buckets.length === 0) return 0;
  return sumMeasure(buckets, key) / buckets.length;
}

/**
 * @param {Array<object>} todayBuckets
 * @param {Record<number, number>|number[]} anticipatedByHour
 * @returns {Array<{ hour: number, label: string, actual: number, anticipated: number }>}
 */
export function mergeAnticipatedActual(todayBuckets, anticipatedByHour) {
  const byHour = new Map();
  for (const bucket of todayBuckets || []) {
    if (!bucket?.bucketStart) continue;
    const hour = new Date(bucket.bucketStart).getUTCHours();
    byHour.set(hour, Number(bucket.enrouteCount) || 0);
  }
  const rows = [];
  for (let hour = 0; hour < 24; hour += 1) {
    const anticipated = Array.isArray(anticipatedByHour)
      ? anticipatedByHour[hour]
      : anticipatedByHour?.[hour];
    rows.push({
      hour,
      label: String(hour).padStart(2, '0'),
      actual: byHour.get(hour) || 0,
      anticipated: Number(anticipated) || 0
    });
  }
  return rows;
}

/**
 * @param {'hour'|'day'|'week'} grain
 * @param {string} bucketStart
 * @returns {string}
 */
export function formatBucketLabel(grain, bucketStart) {
  const date = new Date(bucketStart);
  if (Number.isNaN(date.getTime())) return '';
  if (grain === 'hour') {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      timeZone: 'UTC'
    });
  }
  if (grain === 'week') {
    return `Week of ${date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC'
    })}`;
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
  });
}

/**
 * @param {Array<object>} buckets
 * @param {'hour'|'day'|'week'} grain
 * @returns {Array<object>}
 */
export function volumeChartRows(buckets, grain) {
  return (buckets || []).map((bucket) => ({
    ...bucket,
    label: formatBucketLabel(grain, bucket.bucketStart),
    enrouteCount: Number(bucket.enrouteCount) || 0
  }));
}

/**
 * @param {{ from: Date|string, to: Date|string }} window
 * @returns {{ from: string, to: string }}
 */
export function toIsoWindow(window) {
  return {
    from: new Date(window.from).toISOString(),
    to: new Date(window.to).toISOString()
  };
}

/**
 * @param {unknown} timeZone
 * @returns {string|null}
 */
export function resolveChartTimeZone(timeZone) {
  if (typeof timeZone !== 'string' || !timeZone.trim()) return null;
  const candidate = timeZone.trim();
  try {
    Intl.DateTimeFormat('en-US', { timeZone: candidate });
    return candidate;
  } catch {
    return null;
  }
}

/**
 * Logged-in user's local zone. Compute only — never printed as UTC.
 *
 * @returns {string}
 */
export function viewerTimeZone() {
  try {
    return resolveChartTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone)
      || 'America/New_York';
  } catch {
    return 'America/New_York';
  }
}

/**
 * @param {unknown} timeZone
 * @returns {string}
 */
export function chartTimeZone(timeZone) {
  return resolveChartTimeZone(timeZone) || viewerTimeZone();
}

/**
 * Company view clock. Location is not a fallback.
 *
 * @param {{ timezone?: string|null }|null|undefined} company
 * @returns {string|null}
 */
export function companyClockZone(company) {
  return resolveChartTimeZone(company?.timezone);
}

/**
 * @param {Date|string} value
 * @param {string} timeZone
 * @returns {{ year: number, month: number, day: number, hour: number, minute: number, second: number, weekday: string }}
 */
export function zonedDateParts(value, timeZone) {
  const date = value instanceof Date ? value : new Date(value);
  const tz = chartTimeZone(timeZone);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    weekday: 'short',
    hourCycle: 'h23'
  }).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value;
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: Number(get('hour')),
    minute: Number(get('minute')),
    second: Number(get('second')),
    weekday: get('weekday') || ''
  };
}

/**
 * @param {{ year: number, month: number, day: number, hour?: number, minute?: number, second?: number }} civil
 * @param {string} timeZone
 * @returns {Date}
 */
export function zonedCivilToUtc(civil, timeZone) {
  const tz = chartTimeZone(timeZone);
  const hour = civil.hour || 0;
  const minute = civil.minute || 0;
  const second = civil.second || 0;
  const guess = new Date(Date.UTC(civil.year, civil.month - 1, civil.day, hour, minute, second));
  for (let i = 0; i < 4; i += 1) {
    const shown = zonedDateParts(guess, tz);
    const shownMs = Date.UTC(
      shown.year,
      shown.month - 1,
      shown.day,
      shown.hour,
      shown.minute,
      shown.second
    );
    const wantedMs = Date.UTC(civil.year, civil.month - 1, civil.day, hour, minute, second);
    const delta = wantedMs - shownMs;
    if (delta === 0) break;
    guess.setTime(guess.getTime() + delta);
  }
  return guess;
}

/**
 * @param {Date|string} value
 * @param {string} timeZone
 * @returns {Date}
 */
export function zonedDayStart(value, timeZone) {
  const parts = zonedDateParts(value, timeZone);
  return zonedCivilToUtc({
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: 0
  }, timeZone);
}

/**
 * @param {Date|string} value
 * @param {string} timeZone
 * @returns {Date}
 */
export function zonedHourStart(value, timeZone) {
  const parts = zonedDateParts(value, timeZone);
  return zonedCivilToUtc({
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour
  }, timeZone);
}

/**
 * ISO weekday in the display zone: 1=Mon … 7=Sun.
 *
 * @param {Date|string} value
 * @param {string} timeZone
 * @returns {number}
 */
export function isoWeekdayInTimeZone(value, timeZone) {
  const parts = zonedDateParts(value, timeZone);
  const map = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 7 };
  return map[parts.weekday] || isoWeekdayUtc(value);
}

/**
 * @param {number} isoWeekday
 * @returns {string}
 */
export function weekdayName(isoWeekday) {
  return WEEKDAY_NAMES[Number(isoWeekday) - 1] || 'this weekday';
}

/**
 * @param {Date} start
 * @param {number} days
 * @param {string} timeZone
 * @returns {Date}
 */
export function addZonedDays(start, days, timeZone) {
  const parts = zonedDateParts(start, timeZone);
  const civil = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + days));
  return zonedCivilToUtc({
    year: civil.getUTCFullYear(),
    month: civil.getUTCMonth() + 1,
    day: civil.getUTCDate(),
    hour: 0
  }, timeZone);
}

/**
 * @param {Date} start
 * @param {number} months
 * @param {string} timeZone
 * @returns {Date}
 */
export function addZonedMonths(start, months, timeZone) {
  const parts = zonedDateParts(start, timeZone);
  return zonedCivilToUtc({
    year: parts.year,
    month: parts.month + months,
    day: 1,
    hour: 0
  }, timeZone);
}

/**
 * Exclusive end of the view-zone period that contains `start`.
 *
 * @param {string} filter
 * @param {Date|string} start
 * @param {string} [timeZone]
 * @returns {Date}
 */
export function zonedPeriodEnd(filter, start, timeZone) {
  const tz = chartTimeZone(timeZone);
  if (filter === 'hour') {
    const parts = zonedDateParts(start, tz);
    return zonedCivilToUtc({
      year: parts.year,
      month: parts.month,
      day: parts.day,
      hour: parts.hour + 1
    }, tz);
  }
  if (filter === 'week') return addZonedDays(zonedWeekStart(start, tz), 7, tz);
  if (filter === 'month') return addZonedMonths(zonedMonthStart(start, tz), 1, tz);
  if (filter === 'quarter') return addZonedMonths(zonedQuarterStart(start, tz), 3, tz);
  if (filter === 'year') return addZonedMonths(zonedYearStart(start, tz), 12, tz);
  return addZonedDays(zonedDayStart(start, tz), 1, tz);
}

/**
 * Count exclusive midnights from `from` to `to` in the view zone.
 *
 * @param {Date} from
 * @param {Date} to
 * @param {string} timeZone
 * @returns {number}
 */
export function zonedDaySpan(from, to, timeZone) {
  const tz = chartTimeZone(timeZone);
  let count = 0;
  let cursor = zonedDayStart(from, tz);
  const end = to.getTime();
  while (cursor.getTime() < end && count < 400) {
    cursor = addZonedDays(cursor, 1, tz);
    count += 1;
  }
  return count;
}

/**
 * @param {{ from: Date, to: Date }} window
 * @returns {{ from: Date, to: Date }}
 */
export function clampPipelineWindow(window) {
  const to = new Date(window.to);
  const from = new Date(window.from);
  if (to.getTime() - from.getTime() <= MAX_PIPELINE_MS) {
    return { from, to };
  }
  return { from: new Date(to.getTime() - MAX_PIPELINE_MS), to };
}

/**
 * @param {string} filter
 * @returns {'hour'|'day'|'week'}
 */
export function pipelineGrainForFilter(filter) {
  if (filter === 'month' || filter === 'quarter' || filter === 'year') return 'day';
  if (filter === 'hour' || filter === 'week') return filter;
  return 'day';
}

/**
 * Half-open [from, to) in the display zone, still sent as UTC ISO.
 *
 * @param {'hour'|'day'|'week'|'month'|'quarter'|'year'} filter
 * @param {Date} [now]
 * @param {string} [timeZone]
 * @returns {{ from: Date, to: Date }}
 */
export function windowForFilter(filter, now = new Date(), timeZone) {
  const tz = chartTimeZone(timeZone);
  if (filter === 'hour') {
    const to = new Date(zonedHourStart(now, tz).getTime() + MS_HOUR);
    return { from: new Date(to.getTime() - 24 * MS_HOUR), to };
  }
  if (filter === 'day') {
    const dayStart = zonedDayStart(now, tz);
    const to = addZonedDays(dayStart, 1, tz);
    return { from: addZonedDays(to, -7, tz), to };
  }
  if (filter === 'week') {
    const to = addZonedDays(zonedWeekStart(now, tz), 7, tz);
    return { from: addZonedDays(to, -8 * 7, tz), to };
  }
  if (filter === 'month') {
    const to = addZonedDays(zonedDayStart(now, tz), 1, tz);
    return { from: zonedMonthStart(now, tz), to };
  }
  if (filter === 'quarter') {
    const to = addZonedDays(zonedDayStart(now, tz), 1, tz);
    return { from: zonedQuarterStart(now, tz), to };
  }
  if (filter === 'year') {
    const to = addZonedDays(zonedDayStart(now, tz), 1, tz);
    return { from: zonedYearStart(now, tz), to };
  }
  throw new Error('filter must be hour, day, week, month, quarter, or year');
}

/**
 * @param {Date} [now]
 * @param {string} [timeZone]
 * @returns {{ from: Date, to: Date }}
 */
export function todayZonedWindow(now = new Date(), timeZone) {
  const tz = chartTimeZone(timeZone);
  const from = zonedDayStart(now, tz);
  return { from, to: addZonedDays(from, 1, tz) };
}

/**
 * @param {Date} [now]
 * @param {string} [timeZone]
 * @returns {{ from: Date, to: Date }}
 */
export function sameDayLastWeekZonedWindow(now = new Date(), timeZone) {
  return shiftWindow(todayZonedWindow(now, timeZone), -MS_WEEK);
}

/**
 * Last 12 weeks ending at the start of today in the display zone.
 *
 * @param {Date} [now]
 * @param {string} [timeZone]
 * @returns {{ from: Date, to: Date }}
 */
export function usualLookbackWindow(now = new Date(), timeZone) {
  const today = todayZonedWindow(now, timeZone);
  return {
    from: new Date(today.from.getTime() - USUAL_WEEKS * MS_WEEK),
    to: today.from
  };
}

/**
 * Last 12 weeks ending at this week's start. This week's days stay out of usual.
 *
 * @param {Date} [now]
 * @param {string} [timeZone]
 * @returns {{ from: Date, to: Date }}
 */
export function usualWeekLookbackWindow(now = new Date(), timeZone) {
  const tz = chartTimeZone(timeZone);
  const weekStart = zonedWeekStart(now, tz);
  return {
    from: addZonedDays(weekStart, -USUAL_WEEKS * 7, tz),
    to: weekStart
  };
}

/**
 * View-zone weekday of the stored instant. Do not treat a UTC civil date
 * as a local date.
 *
 * @param {Date|string} bucketStart
 * @param {string} [timeZone]
 * @returns {number}
 */
export function weekdayForDayBucket(bucketStart, timeZone) {
  return isoWeekdayInTimeZone(bucketStart, timeZone);
}

/**
 * UTC asOf for profile=r12 that matches a local hour today.
 *
 * @param {Date|string} now
 * @param {number} localHour
 * @param {string} [timeZone]
 * @returns {Date}
 */
export function r12AsOfForLocalHour(now, localHour, timeZone) {
  const h = Number(localHour);
  if (!Number.isInteger(h) || h < 0 || h > 23) {
    throw new Error('hour must be an integer 0-23');
  }
  const tz = chartTimeZone(timeZone);
  const parts = zonedDateParts(now, tz);
  return zonedCivilToUtc({
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: h
  }, tz);
}

/**
 * @param {unknown} hour
 * @returns {string}
 */
export function formatHourLabel(hour) {
  const h = Number(hour);
  if (!Number.isInteger(h) || h < 0 || h > 23) return '';
  const suffix = h < 12 ? 'am' : 'pm';
  const clock = h % 12 === 0 ? 12 : h % 12;
  return `${clock}${suffix}`;
}

/**
 * @param {object} delta
 * @param {{ unit?: string, versus?: string }} [opts]
 * @returns {string}
 */
export function formatOperatorDelta(delta, { unit = 'tows', versus = 'last week' } = {}) {
  if (!delta || delta.absolute == null) return '—';
  const sign = delta.absolute > 0 ? '+' : '';
  const pct = delta.percent == null ? '' : ` (${sign}${Math.round(delta.percent)}%)`;
  return `${sign}${delta.absolute} ${unit}${pct} vs ${versus}`;
}

/**
 * @param {Array<object>} locations
 * @returns {string|null}
 */
export function timezoneFromLocations(locations) {
  const list = Array.isArray(locations) ? locations : [];
  const primary = list.find((row) => row?.isPrimary) || list[0];
  return resolveChartTimeZone(primary?.timezone || primary?.time_zone);
}

/**
 * @param {Array<{ timezone?: string|null }>} zones
 * @returns {boolean}
 */
export function salesOrgHasMultipleTimeZones(zones) {
  const unique = new Set(
    (zones || [])
      .map((row) => resolveChartTimeZone(row?.timezone))
      .filter(Boolean)
  );
  return unique.size > 1;
}

/**
 * One note when the SO has more than one zone. Never printed on every chart.
 *
 * @param {{ view: 'company'|'all', multiZone: boolean, city?: string|null }} input
 * @returns {string|null}
 */
export function zoneNote({ view, multiZone, city }) {
  if (!multiZone) return null;
  if (view === 'all') return 'Hours in your time';
  const place = typeof city === 'string' ? city.trim() : '';
  if (place) return `Hours in ${place} time`;
  return "Hours in this company's time";
}

/**
 * @param {Array<object>} buckets
 * @param {'month'|'quarter'|'year'} unit
 * @param {string} [timeZone]
 * @returns {Array<object>}
 */
export function pipelineCivilParts(bucketStart) {
  const start = utcDayStart(bucketStart);
  return {
    year: start.getUTCFullYear(),
    month: start.getUTCMonth() + 1,
    day: start.getUTCDate()
  };
}

/**
 * Pipeline day grains are UTC civil dates, not instants to convert. A UTC
 * Tuesday midnight stays Tuesday in Phoenix — do not print it as Monday.
 *
 * @param {Date|string} bucketStart
 * @param {string} [timeZone]
 * @returns {Date}
 */
export function pipelineCivilNoon(bucketStart, timeZone) {
  const civil = pipelineCivilParts(bucketStart);
  return zonedCivilToUtc({
    year: civil.year,
    month: civil.month,
    day: civil.day,
    hour: 12
  }, timeZone);
}

export function rollDayBuckets(buckets, unit, timeZone) {
  const tz = chartTimeZone(timeZone);
  const groups = new Map();
  for (const bucket of buckets || []) {
    if (!bucket?.bucketStart) continue;
    const parts = zonedDateParts(bucket.bucketStart, tz);
    let key;
    let label;
    let start;
    let filter;
    if (unit === 'year') {
      start = zonedYearStart(bucket.bucketStart, tz);
      filter = 'year';
      key = String(parts.year);
      label = String(parts.year);
    } else if (unit === 'quarter') {
      start = zonedQuarterStart(bucket.bucketStart, tz);
      filter = 'quarter';
      const quarter = Math.ceil(parts.month / 3);
      key = `${parts.year}-Q${quarter}`;
      label = `Q${quarter} ${parts.year}`;
    } else {
      start = zonedMonthStart(bucket.bucketStart, tz);
      filter = 'month';
      key = `${parts.year}-${String(parts.month).padStart(2, '0')}`;
      label = start.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
        timeZone: tz
      });
    }
    const end = zonedPeriodEnd(filter, start, tz);
    const current = groups.get(key) || {
      key,
      label,
      bucketStart: start.toISOString(),
      bucketEnd: end.toISOString(),
      enrouteCount: 0,
      completedCount: 0,
      durationWeight: 0,
      durationTotal: 0
    };
    current.enrouteCount += Number(bucket.enrouteCount) || 0;
    current.completedCount += Number(bucket.completedCount) || 0;
    const avg = bucket.avgEnrouteToOnSceneSeconds;
    const weight = Number(bucket.enrouteCount) || 0;
    if (avg != null && weight > 0) {
      current.durationTotal += Number(avg) * weight;
      current.durationWeight += weight;
    }
    groups.set(key, current);
  }
  return [...groups.values()].map((row) => ({
    bucketStart: row.bucketStart,
    bucketEnd: row.bucketEnd,
    label: row.label,
    enrouteCount: row.enrouteCount,
    completedCount: row.completedCount,
    avgEnrouteToOnSceneSeconds: row.durationWeight
      ? row.durationTotal / row.durationWeight
      : null
  }));
}

/**
 * Keep about 12–24 labeled points after rollup.
 *
 * @param {Array<object>} rows
 * @param {number} [max]
 * @returns {Array<object>}
 */
export function capChartRows(rows, max = LABELED_POINT_MAX) {
  if (!Array.isArray(rows) || rows.length <= max) return rows || [];
  const step = Math.ceil(rows.length / max);
  return rows.filter((row, index) => index % step === 0 || index === rows.length - 1);
}

/**
 * @param {string} filter
 * @param {string} bucketStart
 * @param {string} [timeZone]
 * @returns {string}
 */
export function formatFilterBucketLabel(filter, bucketStart, timeZone) {
  const date = new Date(bucketStart);
  if (Number.isNaN(date.getTime())) return '';
  const tz = chartTimeZone(timeZone);
  const parts = zonedDateParts(date, tz);
  if (filter === 'hour') {
    return formatHourLabel(parts.hour);
  }
  if (filter === 'week') {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: tz });
  }
  if (filter === 'month') {
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: tz });
  }
  if (filter === 'quarter') {
    return `Q${Math.ceil(parts.month / 3)} ${parts.year}`;
  }
  if (filter === 'year') {
    return String(parts.year);
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: tz });
}

/**
 * @param {Array<object>} buckets
 * @param {string} filter
 * @param {string} [timeZone]
 * @returns {Array<object>}
 */
export function volumeGrainLabel(filter, bucketStart, timeZone) {
  const date = new Date(bucketStart);
  if (Number.isNaN(date.getTime())) return '';
  const tz = chartTimeZone(timeZone);
  const parts = zonedDateParts(date, tz);
  if (filter === 'month') return String(parts.day);
  if (filter === 'quarter' || filter === 'year') {
    return date.toLocaleDateString('en-US', { month: 'short', timeZone: tz });
  }
  return formatFilterBucketLabel(filter, bucketStart, timeZone);
}

/**
 * October is 31 civil days. Label is the day, not the month number.
 *
 * @param {Array<object>} rows
 * @param {Date} now
 * @param {string} [timeZone]
 * @returns {Array<object>}
 */
export function padMonthDayRows(rows, now, timeZone) {
  const tz = chartTimeZone(timeZone);
  const monthStart = zonedMonthStart(now, tz);
  const monthEnd = addZonedMonths(monthStart, 1, tz);
  const byKey = new Map();
  for (const row of rows || []) {
    if (!row?.bucketStart) continue;
    const parts = zonedDateParts(row.bucketStart, tz);
    byKey.set(`${parts.year}-${parts.month}-${parts.day}`, row);
  }
  const out = [];
  let cursor = monthStart;
  while (cursor.getTime() < monthEnd.getTime() && out.length < 32) {
    const parts = zonedDateParts(cursor, tz);
    const existing = byKey.get(`${parts.year}-${parts.month}-${parts.day}`);
    out.push({
      bucketStart: cursor.toISOString(),
      bucketEnd: addZonedDays(cursor, 1, tz).toISOString(),
      label: String(parts.day),
      enrouteCount: existing ? Number(existing.enrouteCount) || 0 : 0
    });
    cursor = addZonedDays(cursor, 1, tz);
  }
  return out;
}

/**
 * Quarter and year: one point per month. Label is the month, not Q or year.
 *
 * @param {Array<object>} rows
 * @param {string} filter
 * @param {Date} now
 * @param {string} [timeZone]
 * @returns {Array<object>}
 */
export function padMonthNameRows(rows, filter, now, timeZone) {
  const tz = chartTimeZone(timeZone);
  const start = filter === 'year' ? zonedYearStart(now, tz) : zonedQuarterStart(now, tz);
  const months = filter === 'year' ? 12 : 3;
  const byKey = new Map();
  for (const row of rows || []) {
    if (!row?.bucketStart) continue;
    const parts = zonedDateParts(row.bucketStart, tz);
    byKey.set(`${parts.year}-${parts.month}`, row);
  }
  const out = [];
  for (let index = 0; index < months; index += 1) {
    const cursor = addZonedMonths(start, index, tz);
    const parts = zonedDateParts(cursor, tz);
    const existing = byKey.get(`${parts.year}-${parts.month}`);
    out.push({
      bucketStart: cursor.toISOString(),
      bucketEnd: addZonedMonths(cursor, 1, tz).toISOString(),
      label: cursor.toLocaleDateString('en-US', { month: 'short', timeZone: tz }),
      enrouteCount: existing ? Number(existing.enrouteCount) || 0 : 0
    });
  }
  return out;
}

export function volumeChartRowsForFilter(buckets, filter, timeZone, now = new Date()) {
  if (filter === 'month' || filter === 'quarter' || filter === 'year') {
    const raw = (buckets || []).map((bucket) => ({
      ...bucket,
      enrouteCount: Number(bucket.enrouteCount) || 0
    }));
    const padded = filter === 'month'
      ? padMonthDayRows(raw, now, timeZone)
      : padMonthNameRows(raw, filter, now, timeZone);
    return padded.map((bucket) => ({
      ...bucket,
      label: bucket.label || volumeGrainLabel(filter, bucket.bucketStart, timeZone),
      enrouteCount: Number(bucket.enrouteCount) || 0
    }));
  }
  const rolled = (buckets || []).map((bucket) => ({
    ...bucket,
    enrouteCount: Number(bucket.enrouteCount) || 0
  }));
  return capChartRows(rolled).map((bucket) => ({
    ...bucket,
    label: bucket.label || formatFilterBucketLabel(filter, bucket.bucketStart, timeZone),
    enrouteCount: Number(bucket.enrouteCount) || 0
  }));
}

/**
 * Today vs usual overlay in the display zone. Labels are 6am / 7am.
 *
 * @param {Array<object>} todayBuckets
 * @param {Record<number, number>|number[]} usualByLocalHour
 * @param {string} [timeZone]
 * @returns {Array<{ hour: number, label: string, today: number, usual: number }>}
 */
export function mergeTodayVsUsual(todayBuckets, usualByLocalHour, timeZone) {
  const tz = chartTimeZone(timeZone);
  const byHour = new Map();
  for (const bucket of todayBuckets || []) {
    if (!bucket?.bucketStart) continue;
    const hour = zonedDateParts(bucket.bucketStart, tz).hour;
    byHour.set(hour, (byHour.get(hour) || 0) + (Number(bucket.enrouteCount) || 0));
  }
  const rows = [];
  for (let hour = 0; hour < 24; hour += 1) {
    const usual = Array.isArray(usualByLocalHour)
      ? usualByLocalHour[hour]
      : usualByLocalHour?.[hour];
    rows.push({
      hour,
      label: formatHourLabel(hour),
      today: byHour.get(hour) || 0,
      usual: Number(usual) || 0
    });
  }
  return rows;
}

/**
 * @param {Array<{ buckets?: Array<object> }>} results
 * @returns {Array<object>}
 */
export function mergeAggregateBuckets(results) {
  const byStart = new Map();
  for (const result of results || []) {
    for (const bucket of result?.buckets || []) {
      if (!bucket?.bucketStart) continue;
      const current = byStart.get(bucket.bucketStart) || {
        bucketStart: bucket.bucketStart,
        bucketEnd: bucket.bucketEnd,
        enrouteCount: 0,
        completedCount: 0,
        durationWeight: 0,
        durationTotal: 0
      };
      current.enrouteCount += Number(bucket.enrouteCount) || 0;
      current.completedCount += Number(bucket.completedCount) || 0;
      const avg = bucket.avgEnrouteToOnSceneSeconds;
      const weight = Number(bucket.enrouteCount) || 0;
      if (avg != null && weight > 0) {
        current.durationTotal += Number(avg) * weight;
        current.durationWeight += weight;
      }
      if (bucket.bucketEnd) current.bucketEnd = bucket.bucketEnd;
      byStart.set(bucket.bucketStart, current);
    }
  }
  return [...byStart.values()]
    .sort((a, b) => String(a.bucketStart).localeCompare(String(b.bucketStart)))
    .map((row) => ({
      bucketStart: row.bucketStart,
      bucketEnd: row.bucketEnd,
      enrouteCount: row.enrouteCount,
      completedCount: row.completedCount,
      avgEnrouteToOnSceneSeconds: row.durationWeight
        ? row.durationTotal / row.durationWeight
        : null
    }));
}

/**
 * @param {Array<{ companyId: string, name: string, buckets?: Array<object> }>} entries
 * @returns {Array<{ label: string, enrouteCount: number, companyId: string }>}
 */
export function volumeByCompanyRows(entries) {
  return (entries || [])
    .map((entry) => ({
      label: entry.name,
      companyId: entry.companyId,
      enrouteCount: sumMeasure(entry.buckets || [])
    }))
    .sort((a, b) => b.enrouteCount - a.enrouteCount);
}

/**
 * @param {Array<{ name: string, jobs?: Array<object> }>} entries
 * @param {'onScene'|'completed'} metric
 * @returns {Array<{ label: string, seconds: number, display: string }>}
 */
export function responseByCompanyRows(entries, metric) {
  return (entries || [])
    .map((entry) => {
      const seconds = meanDuration(entry.jobs || [], metric);
      return {
        label: entry.name,
        seconds,
        display: formatSeconds(seconds)
      };
    })
    .filter((row) => row.seconds != null)
    .sort((a, b) => b.seconds - a.seconds);
}

/**
 * @param {Array<object>} jobs
 * @param {string} key
 * @returns {Map<string, number>}
 */
export function countByJobField(jobs, key) {
  const counts = new Map();
  for (const job of jobs || []) {
    const value = job?.[key];
    if (value == null || String(value).trim() === '') continue;
    const label = String(value).trim();
    counts.set(label, (counts.get(label) || 0) + 1);
  }
  return counts;
}

/**
 * @param {Map<string, number>} counts
 * @returns {{ key: string, count: number }|null}
 */
export function topCount(counts) {
  let best = null;
  for (const [key, count] of counts || []) {
    if (!best || count > best.count) best = { key, count };
  }
  return best;
}

/**
 * @param {Array<object>} jobs
 * @param {Date} now
 * @param {string} timeZone
 * @returns {Array<object>}
 */
export function jobsOnZonedToday(jobs, now, timeZone) {
  const { from, to } = todayZonedWindow(now, timeZone);
  const start = from.getTime();
  const end = to.getTime();
  return (jobs || []).filter((job) => {
    const stamp = Date.parse(job?.enrouteAt || '');
    return Number.isFinite(stamp) && stamp >= start && stamp < end;
  });
}

/**
 * Same weekday in the display zone, excluding today.
 *
 * @param {Array<object>} jobs
 * @param {Date} now
 * @param {string} timeZone
 * @returns {Array<object>}
 */
export function jobsOnUsualWeekday(jobs, now, timeZone) {
  const weekday = isoWeekdayInTimeZone(now, timeZone);
  const today = todayZonedWindow(now, timeZone);
  return (jobs || []).filter((job) => {
    if (!job?.enrouteAt) return false;
    const stamp = new Date(job.enrouteAt);
    if (Number.isNaN(stamp.getTime())) return false;
    if (stamp >= today.from && stamp < today.to) return false;
    return isoWeekdayInTimeZone(stamp, timeZone) === weekday;
  });
}

/**
 * Calendar date in the display zone. Do not use enrouteAt.slice(0, 10) (UTC).
 *
 * @param {Date|string|null|undefined} value
 * @param {string} [timeZone]
 * @returns {string|null}
 */
export function zonedDateKey(value, timeZone) {
  if (value == null || value === '') return null;
  const stamp = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(stamp.getTime())) return null;
  const parts = zonedDateParts(stamp, timeZone);
  if (![parts.year, parts.month, parts.day].every(Number.isFinite)) return null;
  return `${String(parts.year).padStart(4, '0')}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;
}

/**
 * Mean tows per zoned weekday occurrence. Groups in the display zone.
 *
 * @param {Array<object>} jobs
 * @param {string} [timeZone]
 * @returns {number}
 */
export function usualPerDayAverage(jobs, timeZone) {
  const byDay = new Map();
  for (const job of jobs || []) {
    const day = zonedDateKey(job?.enrouteAt, timeZone);
    if (!day) continue;
    byDay.set(day, (byDay.get(day) || 0) + 1);
  }
  if (byDay.size === 0) return 0;
  return [...byDay.values()].reduce((sum, n) => sum + n, 0) / byDay.size;
}

/**
 * Last 12 of that weekday as count/weeks. Empty weeks stay in the denominator.
 *
 * @param {Array<object>} jobs
 * @param {number} [weeks]
 * @returns {number}
 */
export function usualWeekdayAverage(jobs, weeks = USUAL_WEEKS) {
  const count = Array.isArray(jobs) ? jobs.length : 0;
  const denom = Number(weeks);
  if (!Number.isFinite(denom) || denom <= 0) return 0;
  return count / denom;
}

/**
 * @param {Date|string} value
 * @param {string} [timeZone]
 * @returns {Date}
 */
export function zonedWeekStart(value, timeZone) {
  const tz = chartTimeZone(timeZone);
  const weekday = isoWeekdayInTimeZone(value, tz);
  return addZonedDays(zonedDayStart(value, tz), -(weekday - 1), tz);
}

/**
 * @param {Date|string} value
 * @param {string} [timeZone]
 * @returns {Date}
 */
export function zonedMonthStart(value, timeZone) {
  const tz = chartTimeZone(timeZone);
  const parts = zonedDateParts(value, tz);
  return zonedCivilToUtc({ year: parts.year, month: parts.month, day: 1, hour: 0 }, tz);
}

/**
 * @param {Date|string} value
 * @param {string} [timeZone]
 * @returns {Date}
 */
export function zonedQuarterStart(value, timeZone) {
  const tz = chartTimeZone(timeZone);
  const parts = zonedDateParts(value, tz);
  const month = (Math.ceil(parts.month / 3) - 1) * 3 + 1;
  return zonedCivilToUtc({ year: parts.year, month, day: 1, hour: 0 }, tz);
}

/**
 * @param {Date|string} value
 * @param {string} [timeZone]
 * @returns {Date}
 */
export function zonedYearStart(value, timeZone) {
  const tz = chartTimeZone(timeZone);
  const parts = zonedDateParts(value, tz);
  return zonedCivilToUtc({ year: parts.year, month: 1, day: 1, hour: 0 }, tz);
}

/**
 * Comparison period. Year stays null until the caller has a full year.
 *
 * @param {string} filter
 * @param {Date} [now]
 * @param {string} [timeZone]
 * @returns {{ from: Date, to: Date }|null}
 */
export function comparisonWindowForFilter(filter, now = new Date(), timeZone) {
  const tz = chartTimeZone(timeZone);
  if (filter === 'hour') return todayZonedWindow(now, tz);
  if (filter === 'day') {
    const from = zonedWeekStart(now, tz);
    return { from, to: addZonedDays(from, 7, tz) };
  }
  if (filter === 'week') {
    const to = addZonedDays(zonedWeekStart(now, tz), 7, tz);
    return { from: addZonedDays(to, -13 * 7, tz), to };
  }
  if (filter === 'month') {
    const to = addZonedDays(zonedDayStart(now, tz), 1, tz);
    return clampPipelineWindow({ from: addZonedMonths(zonedMonthStart(now, tz), -4, tz), to });
  }
  if (filter === 'quarter') {
    const to = addZonedDays(zonedDayStart(now, tz), 1, tz);
    return clampPipelineWindow({ from: addZonedMonths(zonedQuarterStart(now, tz), -6, tz), to });
  }
  if (filter === 'year') {
    const to = addZonedDays(zonedDayStart(now, tz), 1, tz);
    return { from: addZonedMonths(zonedYearStart(now, tz), -36, tz), to };
  }
  return null;
}

/**
 * @param {string} filter
 * @returns {'hour'|'day'|'week'|null}
 */
export function comparisonGrainForFilter(filter) {
  if (filter === 'year') return null;
  if (filter === 'hour' || filter === 'week') return filter;
  return 'day';
}

/**
 * Year stays hidden until rolling data crosses one year.
 *
 * @param {string} filter
 * @param {{ hasYear?: boolean }} [opts]
 * @returns {boolean}
 */
export function showsComparisonChart(filter, { hasYear } = {}) {
  if (filter === 'year') return Boolean(hasYear);
  return Boolean(COMPARISON_TITLES[filter]);
}

/**
 * @param {string} filter
 * @returns {string}
 */
export function timelineLabel(filter) {
  return TIMELINE_LABELS[filter] || TIMELINE_LABELS.day;
}

/**
 * @param {string} filter
 * @returns {string|null}
 */
export function comparisonTitle(filter) {
  return COMPARISON_TITLES[filter] || null;
}

/**
 * Company or sales-org name, never a hardcoded string.
 *
 * @param {{ view?: string, companyName?: string|null, salesOrgName?: string|null }} input
 * @returns {string}
 */
export function scope1PageTitle({ view, companyName, salesOrgName } = {}) {
  const raw = view === 'all' ? salesOrgName : companyName;
  const name = typeof raw === 'string' ? raw.trim() : '';
  return name ? `${name} Business Insights` : 'Business Insights';
}

/**
 * @param {'onScene'|'completed'} metric
 * @returns {string}
 */
export function metricDescriptor(metric) {
  return metric === RESPONSE_COMPLETED ? 'Enroute to completed' : 'Enroute to on scene';
}

/**
 * @param {unknown} minutes
 * @returns {string}
 */
export function lateThresholdLabel(minutes) {
  const n = Number(minutes);
  const value = Number.isFinite(n) && n > 0 ? Math.round(n) : DEFAULT_LATE_THRESHOLD_MINUTES;
  return `Over ${value} min`;
}

/**
 * @param {Array<object>} buckets
 * @param {string} [timeZone]
 * @param {number} [weeks]
 * @returns {Record<number, number>}
 */
export function usualByWeekdayFromBuckets(buckets, timeZone, weeks = USUAL_WEEKS) {
  const sums = {
    1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0
  };
  for (const bucket of buckets || []) {
    if (!bucket?.bucketStart) continue;
    const dow = weekdayForDayBucket(bucket.bucketStart, timeZone);
    sums[dow] += Number(bucket.enrouteCount) || 0;
  }
  const denom = Number(weeks) > 0 ? Number(weeks) : USUAL_WEEKS;
  const usual = {};
  for (let dow = 1; dow <= 7; dow += 1) usual[dow] = sums[dow] / denom;
  return usual;
}

/**
 * @param {Array<object>} jobs
 * @param {Date} now
 * @param {string} [timeZone]
 * @param {number} [weeks]
 * @returns {number[]}
 */
export function usualByHourFromJobs(jobs, now, timeZone, weeks = USUAL_WEEKS) {
  const weekday = isoWeekdayInTimeZone(now, timeZone);
  const sums = Array.from({ length: 24 }, () => 0);
  for (const job of jobs || []) {
    if (!job?.enrouteAt) continue;
    if (isoWeekdayInTimeZone(job.enrouteAt, timeZone) !== weekday) continue;
    const hour = zonedDateParts(job.enrouteAt, timeZone).hour;
    if (hour >= 0 && hour <= 23) sums[hour] += 1;
  }
  const denom = Number(weeks) > 0 ? Number(weeks) : USUAL_WEEKS;
  return sums.map((count) => count / denom);
}

/**
 * Hour comparison: elapsed hours keep today + usual. Remaining hours usual only.
 *
 * @param {Array<object>} todayBuckets
 * @param {Record<number, number>|number[]} usualByLocalHour
 * @param {Date} now
 * @param {string} [timeZone]
 * @returns {Array<object>}
 */
export function mergeHourComparison(todayBuckets, usualByLocalHour, now, timeZone) {
  const rows = mergeTodayVsUsual(todayBuckets, usualByLocalHour, timeZone);
  const currentHour = zonedDateParts(now, timeZone).hour;
  return rows.map((row) => ({
    ...row,
    today: row.hour <= currentHour ? row.today : null,
    inProgress: row.hour === currentHour
  }));
}

/**
 * This week Mon–Sun. Days through today are actuals. Remaining days are usual only.
 *
 * @param {Array<object>} thisWeekBuckets
 * @param {Record<number, number>} usualByWeekday
 * @param {Date} now
 * @param {string} [timeZone]
 * @returns {Array<object>}
 */
export function mergeWeekdayComparison(thisWeekBuckets, usualByWeekday, now, timeZone) {
  const tz = chartTimeZone(timeZone);
  const todayDow = isoWeekdayInTimeZone(now, tz);
  const actual = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 };
  for (const bucket of thisWeekBuckets || []) {
    if (!bucket?.bucketStart) continue;
    const dow = weekdayForDayBucket(bucket.bucketStart, tz);
    actual[dow] += Number(bucket.enrouteCount) || 0;
  }
  return WEEKDAY_SHORT.map((label, index) => {
    const dow = index + 1;
    const elapsed = dow <= todayDow;
    return {
      weekday: dow,
      label,
      today: elapsed ? actual[dow] : null,
      usual: Number(usualByWeekday?.[dow]) || 0,
      inProgress: dow === todayDow
    };
  });
}

/**
 * @param {string} filter
 * @param {Date|string} value
 * @param {string} [timeZone]
 * @returns {string|null}
 */
export function periodKeyForFilter(filter, value, timeZone) {
  const parts = zonedDateParts(value, timeZone);
  if (filter === 'week') {
    const grain = utcWeekStart(value);
    const mid = new Date(grain.getTime() + 12 * MS_HOUR);
    const start = zonedWeekStart(mid, timeZone);
    const p = zonedDateParts(start, timeZone);
    return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
  }
  if (filter === 'month') return `${parts.year}-${String(parts.month).padStart(2, '0')}`;
  if (filter === 'quarter') return `${parts.year}-Q${Math.ceil(parts.month / 3)}`;
  if (filter === 'year') return String(parts.year);
  return null;
}

/**
 * Prior periods stay blue. The current in-progress period is orange.
 * Thin history — no padded usual bars.
 *
 * @param {Array<object>} buckets
 * @param {string} filter
 * @param {Date} now
 * @param {string} [timeZone]
 * @returns {Array<object>}
 */
export function historyComparisonRows(buckets, filter, now, timeZone) {
  const tz = chartTimeZone(timeZone);
  const rolled = filter === 'week'
    ? (buckets || []).map((bucket) => ({
      ...bucket,
      enrouteCount: Number(bucket.enrouteCount) || 0
    }))
    : rollDayBuckets(buckets, filter, tz);
  const currentKey = periodKeyForFilter(filter, now, tz);
  return rolled.map((row) => {
    const key = periodKeyForFilter(filter, row.bucketStart, tz);
    const inProgress = Boolean(currentKey) && key === currentKey;
    return {
      ...row,
      label: row.label || formatFilterBucketLabel(filter, row.bucketStart, tz),
      tows: Number(row.enrouteCount) || 0,
      color: inProgress ? TODAY_COLOR : USUAL_COLOR,
      inProgress
    };
  });
}

/**
 * Re-bucket pipeline grains on the view-zone civil date of the stored
 * instant. bucketEnd is the end of that same zone period. Do not label a
 * UTC day as a local date. Do not keep the UTC bucketEnd.
 *
 * @param {Array<object>} buckets
 * @param {'hour'|'day'|'week'} grain
 * @param {string} [timeZone]
 * @returns {Array<object>}
 */
export function rebucketPipelineBuckets(buckets, grain, timeZone) {
  const tz = chartTimeZone(timeZone);
  const filter = grain === 'hour' || grain === 'week' ? grain : 'day';
  const groups = new Map();
  for (const bucket of buckets || []) {
    if (!bucket?.bucketStart) continue;
    let key;
    let start;
    if (grain === 'hour') {
      start = zonedHourStart(bucket.bucketStart, tz);
      const parts = zonedDateParts(start, tz);
      key = `${parts.year}-${parts.month}-${parts.day}T${parts.hour}`;
    } else if (grain === 'week') {
      start = zonedWeekStart(bucket.bucketStart, tz);
      key = zonedDateKey(start, tz);
    } else {
      start = zonedDayStart(bucket.bucketStart, tz);
      key = zonedDateKey(start, tz);
    }
    const end = zonedPeriodEnd(filter, start, tz);
    const current = groups.get(key) || {
      bucketStart: start.toISOString(),
      bucketEnd: end.toISOString(),
      enrouteCount: 0,
      completedCount: 0
    };
    current.enrouteCount += Number(bucket.enrouteCount) || 0;
    current.completedCount += Number(bucket.completedCount) || 0;
    groups.set(key, current);
  }
  return [...groups.values()].sort((a, b) => String(a.bucketStart).localeCompare(String(b.bucketStart)));
}

/**
 * Company day is midnight to midnight in companies.timezone. Stamps are
 * not shifted. bucketEnd is the end of that same view-zone period.
 *
 * @param {Array<object>} jobs
 * @param {string} filter
 * @param {string} [timeZone]
 * @returns {Array<object>}
 */
export function volumeBucketsFromJobs(jobs, filter, timeZone) {
  const tz = chartTimeZone(timeZone);
  const groups = new Map();
  for (const job of jobs || []) {
    if (!job?.enrouteAt) continue;
    const stamp = new Date(job.enrouteAt);
    if (Number.isNaN(stamp.getTime())) continue;
    let start;
    let key;
    if (filter === 'hour') {
      start = zonedHourStart(stamp, tz);
      const parts = zonedDateParts(start, tz);
      key = `${parts.year}-${parts.month}-${parts.day}T${parts.hour}`;
    } else if (filter === 'week') {
      start = zonedWeekStart(stamp, tz);
      key = zonedDateKey(start, tz);
    } else if (filter === 'month') {
      start = zonedDayStart(stamp, tz);
      key = zonedDateKey(start, tz);
    } else if (filter === 'quarter' || filter === 'year') {
      start = zonedMonthStart(stamp, tz);
      key = periodKeyForFilter('month', stamp, tz);
    } else {
      start = zonedDayStart(stamp, tz);
      key = zonedDateKey(start, tz);
    }
    if (!key) continue;
    const current = groups.get(key) || {
      bucketStart: start.toISOString(),
      bucketEnd: zonedPeriodEnd(filter, start, tz).toISOString(),
      enrouteCount: 0
    };
    current.enrouteCount += 1;
    groups.set(key, current);
  }
  return [...groups.values()].sort((a, b) => String(a.bucketStart).localeCompare(String(b.bucketStart)));
}

/**
 * Volume points from the loaded window jobs. Failed or missing jobs is
 * not a zero. All, Police, and Regular share this path.
 *
 * @param {object} input
 * @returns {Array<object>|null}
 */
export function volumeRowsForAccountType({
  jobs,
  jobsFailed,
  filter,
  timeZone,
  now
}) {
  if (jobsFailed || jobs == null) return null;
  return volumeChartRowsForFilter(
    volumeBucketsFromJobs(jobs, filter, timeZone),
    filter,
    timeZone,
    now
  );
}

/**
 * Same-length window immediately before the current Time window.
 *
 * @param {string} filter
 * @param {Date} [now]
 * @param {string} [timeZone]
 * @returns {{ from: Date, to: Date }|null}
 */
export function previousWindowForFilter(filter, now = new Date(), timeZone) {
  const tz = chartTimeZone(timeZone);
  if (filter === 'hour') {
    const current = windowForFilter('hour', now, tz);
    return { from: new Date(current.from.getTime() - 24 * MS_HOUR), to: current.from };
  }
  if (filter === 'day') {
    const current = windowForFilter('day', now, tz);
    return { from: addZonedDays(current.from, -7, tz), to: current.from };
  }
  if (filter === 'week') {
    const current = windowForFilter('week', now, tz);
    return { from: addZonedDays(current.from, -8 * 7, tz), to: current.from };
  }
  if (filter === 'month' || filter === 'quarter' || filter === 'year') {
    const current = windowForFilter(filter, now, tz);
    const months = filter === 'year' ? 12 : filter === 'quarter' ? 3 : 1;
    const priorFrom = addZonedMonths(current.from, -months, tz);
    const priorPeriodEnd = current.from;
    const priorTo = addZonedDays(priorFrom, zonedDaySpan(current.from, current.to, tz), tz);
    return {
      from: priorFrom,
      to: priorTo.getTime() > priorPeriodEnd.getTime() ? priorPeriodEnd : priorTo
    };
  }
  return null;
}

/**
 * @param {string} filter
 * @returns {string}
 */
export function previousVersusLabel(filter) {
  if (filter === 'hour') return 'the prior 24 hours';
  if (filter === 'day') return 'last week';
  if (filter === 'week') return 'the prior 8 weeks';
  if (filter === 'month') return 'the same days last month';
  if (filter === 'quarter') return 'the same days last quarter';
  if (filter === 'year') return 'the same days last year';
  return 'the prior window';
}

/**
 * Title follows the Time filter. Not Performance Review.
 *
 * @param {string} filter
 * @returns {string}
 */
export function performanceTitle(filter) {
  return PERFORMANCE_TITLES[filter] || PERFORMANCE_TITLES.day;
}

/**
 * @param {number} delta
 * @returns {'up'|'down'|'flat'}
 */
export function performanceDirection(delta) {
  if (delta > 0) return 'up';
  if (delta < 0) return 'down';
  return 'flat';
}

/**
 * One sentence with absolute and percent. Failed previous is not a fake zero.
 *
 * @param {object} input
 * @returns {string|null}
 */
export function tileTrendSentence({
  current,
  previous,
  unit = 'tows',
  versus = 'last week',
  weekdayCurrent,
  weekdayPrevious,
  weekday
} = {}) {
  const delta = deltaPair(current, previous);
  if (delta.absolute == null && weekdayCurrent == null) return null;
  const parts = [];
  if (delta.absolute != null) {
    if (delta.absolute === 0) {
      parts.push(`Same as ${versus}`);
    } else if (unit === 'min') {
      const faster = delta.absolute > 0 ? 'slower' : 'faster';
      const pct = delta.percent == null ? '' : ` (${Math.abs(Math.round(delta.percent))}% ${delta.absolute > 0 ? 'above' : 'below'})`;
      parts.push(`${Math.abs(delta.absolute)} min ${faster} than ${versus}${pct}`);
    } else {
      const pct = delta.percent == null
        ? ''
        : `${Math.abs(Math.round(delta.percent))}% ${delta.absolute > 0 ? 'above' : 'below'} ${versus}. `;
      const more = delta.absolute > 0 ? 'more' : (unit === 'tows' ? 'fewer' : 'less');
      parts.push(`${pct}${Math.abs(delta.absolute)} ${more} than ${versus}`.trim());
    }
  }
  const day = weekdayName(weekday);
  const weekdayDelta = deltaPair(weekdayCurrent, weekdayPrevious);
  if (weekdayDelta.absolute != null && weekday) {
    if (weekdayDelta.absolute === 0) {
      parts.push(`same as last ${day}`);
    } else {
      const more = weekdayDelta.absolute > 0 ? 'more' : 'fewer';
      parts.push(`${Math.abs(weekdayDelta.absolute)} ${more} than last ${day}`);
    }
  }
  if (parts.length === 0) return null;
  const text = parts.join('. ');
  return text.endsWith('.') ? text : `${text}.`;
}

/**
 * One KPI sentence. Direction follows the tows number. Failed previous is
 * not a fake zero. Says tows, not units.
 *
 * @param {object} input
 * @returns {string|null}
 */
export function performanceSentence({
  current,
  previous,
  versus = 'last week',
  weekdayCurrent,
  weekdayPrevious,
  weekday
} = {}) {
  const delta = deltaPair(current, previous);
  const weekdayDelta = deltaPair(weekdayCurrent, weekdayPrevious);
  const parts = [];
  if (delta.absolute != null) {
    const dir = performanceDirection(delta.absolute);
    if (dir === 'flat') {
      parts.push(`Performance is flat from ${versus} (0 tows)`);
    } else {
      const pct = delta.percent == null ? '' : ` ${Math.abs(Math.round(delta.percent))}%`;
      const signed = delta.absolute > 0 ? `+${delta.absolute}` : `−${Math.abs(delta.absolute)}`;
      parts.push(`Performance is ${dir}${pct} from ${versus} (${signed} tows)`);
    }
  }
  if (weekdayDelta.absolute != null && weekday) {
    const day = weekdayName(weekday);
    if (weekdayDelta.absolute === 0) {
      parts.push(`today is the same as last ${day}`);
    } else {
      const cmp = weekdayDelta.absolute > 0 ? 'higher' : 'lower';
      parts.push(`today is ${Math.abs(weekdayDelta.absolute)} tows ${cmp} than last ${day}`);
    }
  }
  if (parts.length === 0) return null;
  return `${parts.join(', and ')}.`;
}

/**
 * @param {object} input
 * @returns {string|null}
 */
export function pieLateSentence({ lateRate: rate, usualLateRate, weekday } = {}) {
  if (rate == null) return null;
  const todayPct = Math.round(Number(rate) * 100);
  if (usualLateRate == null) return `${todayPct}% late.`;
  return `${todayPct}% late. Usual ${weekdayName(weekday)} is ${Math.round(Number(usualLateRate) * 100)}%.`;
}

/**
 * @param {Array<object>} jobs
 * @param {Date} now
 * @param {string} [timeZone]
 * @returns {Array<object>}
 */
export function todayHourBucketsFromJobs(jobs, now, timeZone) {
  const tz = chartTimeZone(timeZone);
  const today = todayZonedWindow(now, tz);
  const byHour = Array.from({ length: 24 }, () => 0);
  for (const job of jobs || []) {
    const stamp = Date.parse(job?.enrouteAt || '');
    if (!Number.isFinite(stamp) || stamp < today.from.getTime() || stamp >= today.to.getTime()) {
      continue;
    }
    const hour = zonedDateParts(stamp, tz).hour;
    if (hour >= 0 && hour <= 23) byHour[hour] += 1;
  }
  const parts = zonedDateParts(now, tz);
  return byHour.map((enrouteCount, hour) => ({
    bucketStart: zonedCivilToUtc({
      year: parts.year,
      month: parts.month,
      day: parts.day,
      hour
    }, tz).toISOString(),
    enrouteCount
  }));
}

/**
 * @param {Array<object>} jobs
 * @param {Date} now
 * @param {string} [timeZone]
 * @returns {Array<object>}
 */
export function thisWeekBucketsFromJobs(jobs, now, timeZone) {
  const tz = chartTimeZone(timeZone);
  const weekStart = zonedWeekStart(now, tz);
  const weekEnd = addZonedDays(weekStart, 7, tz);
  const counts = {
    1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0
  };
  for (const job of jobs || []) {
    const stamp = Date.parse(job?.enrouteAt || '');
    if (!Number.isFinite(stamp) || stamp < weekStart.getTime() || stamp >= weekEnd.getTime()) {
      continue;
    }
    counts[isoWeekdayInTimeZone(stamp, tz)] += 1;
  }
  return [1, 2, 3, 4, 5, 6, 7].map((dow) => ({
    bucketStart: addZonedDays(weekStart, dow - 1, tz).toISOString(),
    enrouteCount: counts[dow]
  }));
}

/**
 * @param {Array<object>} jobs
 * @param {string} [timeZone]
 * @param {number} [weeks]
 * @returns {Record<number, number>}
 */
export function usualByWeekdayFromJobs(jobs, timeZone, weeks = USUAL_WEEKS) {
  const sums = {
    1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0
  };
  for (const job of jobs || []) {
    if (!job?.enrouteAt) continue;
    sums[isoWeekdayInTimeZone(job.enrouteAt, timeZone)] += 1;
  }
  const denom = Number(weeks) > 0 ? Number(weeks) : USUAL_WEEKS;
  const usual = {};
  for (let dow = 1; dow <= 7; dow += 1) usual[dow] = sums[dow] / denom;
  return usual;
}

/**
 * @param {Array<object>} jobs
 * @param {{ from: Date, to: Date }} window
 * @returns {Array<object>}
 */
export function jobsInWindow(jobs, window) {
  if (!window?.from || !window?.to) return [];
  const start = window.from.getTime();
  const end = window.to.getTime();
  return (jobs || []).filter((job) => {
    const stamp = Date.parse(job?.enrouteAt || '');
    return Number.isFinite(stamp) && stamp >= start && stamp < end;
  });
}
